import { CartAddEvent } from '@theme/events';

/**
 * Free-shipping band + cross-sell row for the cart drawer.
 *
 * Liquid renders the band, the footer Shipping row and the full candidate pool
 * (see cart-shipping-bar / cart-summary / cart-crosssell). This module owns
 * every number and every string that depends on the remaining gap, because the
 * threshold is per-visitor and only the browser knows it.
 *
 * THRESHOLD, in order of authority:
 *   1. Intelligems. The live "Free Shipping Test" assigns each visitor an arm
 *      ($100 / $125 / $150) and applies a 100% shipping discount at that arm's
 *      minimum. Read from window.igData.offers.getAll().
 *   2. The Shopify shipping rate, mirrored in a theme setting, used for the
 *      control arm - which gets no Intelligems offer at all - and whenever
 *      Intelligems is missing or blocked.
 * The fallback is the highest arm, so before Intelligems resolves the drawer
 * only ever overstates the gap. It never promises free shipping it cannot
 * deliver, which is the only direction that is safe to be wrong in.
 *
 * REDO. The Redo coverage line is a real cart line and Shopify counts it
 * toward the rate condition - verified live: a $149.00 cart quotes $7.99
 * Ground, and adding a $2.50 Redo line takes it to $151.50 and $0.00. So the
 * gap is measured on the full cart total including Redo, which is what
 * checkout will do. The lead-tile affordability guard uses the merchandise
 * subtotal instead: a $2.50 insurance fee is not evidence of buying power.
 * Redo also mutates the cart outside the theme's events, so its toggle is
 * picked up by the click listener below rather than by cart:update.
 */

const SELECTORS = {
  band: '[data-sene-band]',
  bandHeadline: '[data-sene-band-headline]',
  bandFill: '[data-sene-band-fill]',
  xsell: '[data-sene-xsell]',
  strip: '[data-xsell-strip]',
  tile: '[data-xsell-tile]',
  eyebrow: '[data-xsell-eyebrow]',
  note: '[data-xsell-note]',
  helper: '[data-xsell-helper]',
  add: '[data-xsell-add]',
  options: '[data-xsell-options]',
  optionsClose: '[data-xsell-options-close]',
  optionValue: '[data-xsell-option-value]',
  confirm: '[data-xsell-confirm]',
  variants: '[data-xsell-variants]',
  price: '.sene-xsell__price',
  shippingRow: '[data-sene-shipping-row]',
  shippingValue: '[data-sene-shipping-value]',
  emptyThreshold: '[data-sene-empty-threshold]',
  drawer: '.cart-drawer__dialog',
  scroller: '.cart-drawer__items',
  // Shopify's accelerated-checkout web component. `shop-pay-wallet-button` is
  // the element that actually renders the button; `.additional-checkout-buttons`
  // is the wrapper the theme puts around it, kept as a fallback in case Shopify
  // ever renders a different element for it.
  shopPayButton: 'shop-pay-wallet-button, .additional-checkout-buttons',
};

/** Intelligems experience id for the live free-shipping threshold test. */
const IG_SHIPPING_EXPERIENCE_ID = '678b9f37-e4d6-4764-b81e-504aefdbe528';

/** Redo's coverage product. Matched on any of three signals so that Redo
 *  swapping price variants, or the product being duplicated, cannot make the
 *  line read as merchandise. */
const REDO_PRODUCT_ID = 8057521471685;
const REDO_VENDOR = 're:do';
const REDO_SKU = 'x-redo';

const COPY = {
  clearedHeadline: 'Free shipping unlocked',
  gapHeadline: (money) => `You are ${money} from free shipping.`,
  eyebrowLead: 'Add to get free shipping',
  eyebrowPlain: 'Goes with what you picked',
  noteAllClear: (money) => `Any of these takes the order past ${money}, so shipping is free.`,
  noteClearedWith: (item) => `Goes with a ${item}. Shipping is already free.`,
  noteCleared: 'Shipping is already free.',
  helperClears: 'Clears the gap',
  helperShort: (money) => `Still ${money} short`,
  shippingFree: 'Free',
  shippingPending: 'Calculated at checkout',
  addError: 'That one just sold out. Pick another and we will keep the rest of your cart.',
  fitError: 'We could not confirm your saved fit for this style, so we have not added it. You can pick your fit on the product page.',
};

let cart = null;
let painting = false;
let cartFetch = null;
/** Scroll offsets to put back after an add. The section morph replaces both
 *  scrollers, so a single restore on the next frame races the re-render and
 *  loses. These are re-applied on every paint until they stick. */
let pendingScroll = null;
let seenImpressions = new Set();
let impressionObserver = null;
let addInFlight = false;

/* ---------------------------------------------------------------- money --- */

let moneyFormat = '${{amount}}';

/**
 * Mirrors the theme's money filter, using the same shop.money_format the cart
 * summary reconciler uses so the two never disagree.
 *
 * @param {number} cents
 * @param {'shop'|'always'|'never'} [decimals]
 *   'shop'   - whatever shop.money_format says.
 *   'always' - force cents. Used for shortfalls, which must never round:
 *              telling someone they are "$4 short" when they are $3.50 short
 *              swaps one lie for another.
 *   'never'  - drop cents. Used for whole amounts, matching the
 *              money_without_trailing_zeros filter Liquid uses in the drawer,
 *              so "$125.00" never appears next to Liquid's "$125".
 * @returns {string}
 */
function money(cents, decimals = 'shop') {
  if (cents == null || Number.isNaN(cents)) return '';

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  const withDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
    const fixed = (value / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return dollars + (parts[1] ? decimal + parts[1] : '');
  };

  const match = moneyFormat.match(placeholderRegex);
  let placeholder = match ? match[1] : 'amount';

  if (decimals === 'always') {
    placeholder = placeholder
      .replace('amount_no_decimals_with_comma_separator', 'amount_with_comma_separator')
      .replace('amount_no_decimals_with_space_separator', 'amount_with_space_separator')
      .replace('amount_no_decimals', 'amount');
  } else if (decimals === 'never') {
    placeholder = placeholder
      .replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator')
      .replace('amount_with_space_separator', 'amount_no_decimals_with_space_separator')
      .replace(/^amount$/, 'amount_no_decimals');
  }

  let value;
  switch (placeholder) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', '');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2);
      break;
  }

  return moneyFormat.replace(placeholderRegex, value);
}

/** Whole-currency amounts drop their trailing zeros, matching the design and
 *  the money_without_trailing_zeros filter Liquid uses everywhere else in this
 *  drawer. Part amounts keep their cents rather than rounding. */
function moneyTrim(cents) {
  return cents % 100 === 0 ? money(cents, 'never') : money(cents, 'always');
}

/* ------------------------------------------------------------ threshold --- */

/**
 * The visitor's assigned free-shipping minimum, in cents, from Intelligems.
 * Reads the offer's own tier rather than the arm's NAME: arm names are free
 * text a human can rename, the tier is the number that actually applies.
 * @returns {number|null}
 */
function intelligemsThresholdCents() {
  try {
    const offers = window.igData?.offers?.getAll?.();
    if (!Array.isArray(offers)) return null;

    let lowest = null;

    for (const entry of offers) {
      const offer = entry?.offer;

      // Deliberately NOT gated on offer.enabled. Intelligems marks the control
      // arm's offer disabled because it applies no discount of its own, but the
      // arm still stands for a threshold - $100 here - and every surface must
      // state the threshold of the arm the visitor is actually in. Gating on
      // `enabled` made control fall back to the native $150, so the bar and the
      // header disagreed with the arm. (Ray, 2026-09-03.)
      //
      // `unitType === 'dollar'` still excludes the unit-based gift-with-purchase
      // offers, which are the only other things this returns.
      if (!offer || offer.unitType !== 'dollar' || offer.isArchived) continue;

      for (const tier of offer.tiers || []) {
        if (!tier?.isFreeShipping) continue;
        const units = Number(tier.minimumUnits);
        if (!Number.isFinite(units) || units <= 0) continue;
        if (lowest === null || units < lowest) lowest = units;
      }
    }

    return lowest === null ? null : Math.round(lowest * 100);
  } catch (error) {
    return null;
  }
}

/**
 * Any element rendered by cart-threshold-config carries the server-side
 * fallback. The band only exists when the cart has items, but the announcement
 * bar is on every page, so look this up globally rather than off the band.
 * @returns {HTMLElement|null}
 */
function configElement() {
  return document.querySelector('[data-fallback-threshold-cents]');
}

/** Money formatting is driven by shop.money_format, rendered onto the config
 *  element. Pulled in before any formatting so drift checks compare against the
 *  same string paint() would write, rather than the module's default. */
function syncMoneyFormat() {
  const config = configElement();
  if (config?.dataset.moneyFormat) moneyFormat = config.dataset.moneyFormat;
}

/**
 * @returns {{cents: number, source: string}}
 */
function resolveThreshold() {
  const ig = intelligemsThresholdCents();
  if (ig) return { cents: ig, source: 'intelligems' };

  const config = configElement();
  const fallback = Number(config?.dataset.fallbackThresholdCents);
  if (Number.isFinite(fallback) && fallback > 0) {
    return { cents: fallback, source: config?.dataset.thresholdSource || 'fallback' };
  }

  return { cents: 15000, source: 'constant' };
}

/* ----------------------------------------------------------------- cart --- */

/** @param {object} item A line item from /cart.js */
function isRedoLine(item) {
  return (
    item.product_id === REDO_PRODUCT_ID ||
    String(item.vendor || '').toLowerCase() === REDO_VENDOR ||
    String(item.sku || '') === REDO_SKU
  );
}

function merchandiseLines() {
  return (cart?.items || []).filter((item) => !isRedoLine(item));
}

/** Total the threshold is measured against: after discounts, before shipping
 *  and tax, Redo included, because that is what Shopify's rate condition
 *  tests. */
function cartTotalCents() {
  return Number(cart?.total_price) || 0;
}

/** Merchandise-only subtotal, used for the lead-tile affordability guard. */
function merchandiseSubtotalCents() {
  return merchandiseLines().reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);
}

let lastCartFetchAt = 0;

async function refreshCart() {
  // Collapse concurrent refreshes so a burst of cart events costs one request.
  if (cartFetch) return cartFetch;
  lastCartFetchAt = Date.now();

  cartFetch = fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => response.json())
    .then((data) => {
      cart = data;
      return data;
    })
    .catch(() => cart)
    .finally(() => {
      cartFetch = null;
    });

  return cartFetch;
}

/* ---------------------------------------------------------------- paint --- */

/**
 * Describes the single cart item the cleared-state note refers to, e.g.
 * "black Cumulus Crew T-Shirt". Returns null when the cart holds more than one
 * product, because the design's copy asserts a fact about one garment and
 * asserting it about a mixed cart would be false.
 * @returns {string|null}
 */
function clearedNoteSubject() {
  const lines = merchandiseLines();
  if (lines.length !== 1) return null;

  const line = lines[0];
  const title = line.product_title || line.title;
  if (!title) return null;

  const colourOption = (line.options_with_values || []).find((option) =>
    /colou?r/i.test(option?.name || '')
  );
  const colour = colourOption?.value;

  return colour ? `${String(colour).toLowerCase()} ${title}` : title;
}

/* Black Friday "buy more, save more" steps for BF30. Same figures as the
 * tier strip above the header (sections/bf-tier-strip.liquid), in cents. */
const BF30_TIERS = [
  // The 20% tier has no minimum, so it is unlocked by any cart with items in it.
  { min: 1, pct: 20 },
  { min: 30000, pct: 25 },
  { min: 60000, pct: 30 },
];
/** Share of the track given to the free-shipping stretch; the BF tiers get the rest. */
const BF30_SPLIT = 30;

/** True once the shopper has BF30 in the cart, whether or not it is the code
 *  being charged yet, so the steps show before the first tier is reached. */
function bf30Entered() {
  return (cart?.discount_codes || []).some((discount) => String(discount.code).toUpperCase() === 'BF30');
}

/** True only while BF30 is the code actually being charged. */
function bf30Winning() {
  return (cart?.discount_codes || []).some(
    (discount) => String(discount.code).toUpperCase() === 'BF30' && discount.applicable
  );
}

/** Track position, 0-100, for a cents value. Free shipping fills the first
 *  BF30_SPLIT percent. The BF30 steps then sit at fixed, evenly spaced
 *  positions, so the dots never crowd each other. */
function bf30Position(cents, thresholdCents) {
  const anchors = [
    [0, 0],
    [thresholdCents, BF30_SPLIT],
    ...BF30_TIERS.slice(1).map((tier, index) => [tier.min, BF30_SPLIT + 20 * (index + 2)]),
  ];
  for (let i = 1; i < anchors.length; i++) {
    const [fromCents, fromPct] = anchors[i - 1];
    const [toCents, toPct] = anchors[i];
    if (cents <= toCents) {
      if (toCents === fromCents) return toPct;
      return fromPct + ((cents - fromCents) / (toCents - fromCents)) * (toPct - fromPct);
    }
  }
  return anchors[anchors.length - 1][1];
}

/** True when the drawer should show the tier band: the shopper came through the BF30 link and no
 *  other code is the one being charged. Otherwise the plain free-shipping band stays, because the
 *  tiers would promise discounts checkout will not apply. */
function bf30BandActive() {
  if (!document.documentElement.classList.contains('bf30')) return false;
  return !(cart?.discount_codes || []).some(
    (discount) => String(discount.code).toUpperCase() !== 'BF30' && discount.applicable
  );
}

/** Caraway-style band: one message over a row of segments, each filling as the
 *  shopper closes in on it. Tiers read the pre-discount subtotal, the same basis
 *  the discount function uses. Free shipping reads what the shopper actually
 *  pays, because that is what the shipping rate looks at. */
function paintBf30Band(band, { gapCents, cleared, thresholdCents }) {
  const subtotal = Number(cart?.original_total_price) || 0;
  const hasShipping = thresholdCents > 0;
  const paid = cartTotalCents();

  let message;
  if (subtotal <= 0) message = 'Add anything to unlock 20% off';
  else if (hasShipping && !cleared) message = `Spend ${moneyTrim(gapCents)} more to unlock free shipping`;
  else if (subtotal < 30000) message = `Spend ${moneyTrim(30000 - subtotal)} more to unlock 25% off`;
  else if (subtotal < 60000) message = `Spend ${moneyTrim(60000 - subtotal)} more to unlock 30% off`;
  else message = '30% off unlocked, our biggest discount';

  const ratio = (value, from, to) => (to <= from ? 1 : Math.max(0, Math.min(1, (value - from) / (to - from))));
  const shippingDone = cleared ? 1 : hasShipping ? Math.min(ratio(paid, 0, thresholdCents), 0.99) : 0;
  const segments = [
    { label: '20% Off', fill: subtotal > 0 ? 1 : 0 },
    ...(hasShipping ? [{ label: 'Free Shipping', fill: shippingDone }] : []),
    { label: '25% Off', fill: ratio(subtotal, hasShipping ? thresholdCents : 0, 30000) },
    { label: '30% Off', fill: ratio(subtotal, 30000, 60000) },
  ];

  let root = band.querySelector('[data-bf30-band]');
  if (!root) {
    root = document.createElement('div');
    root.className = 'sene-bfband';
    root.dataset.bf30Band = '';
    band.append(root);
  }
  const check =
    '<svg class="sene-bfband__check" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="currentColor"/><path d="M4.8 8.2l2.2 2.2 4.2-4.4" fill="none" stroke="#3f5d3a" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  root.innerHTML =
    `<p class="sene-bfband__msg" aria-live="polite">${message}</p><div class="sene-bfband__segs">` +
    segments
      .map((segment) => {
        const done = segment.fill >= 1;
        return (
          `<div class="sene-bfband__seg${done ? ' is-done' : ''}">` +
          `<span class="sene-bfband__fill" style="width:${(segment.fill * 100).toFixed(1)}%"></span>` +
          `<span class="sene-bfband__label">${done ? check : ''}` +
          `${segment.label}</span>` +
          '</div>'
        );
      })
      .join('') +
    '</div>';
}

function paintBf30Marks(band, applied, thresholdCents, fillPct) {
  let marks = band.querySelector('[data-bf30-marks]');
  const usable = thresholdCents > 0;
  if (!applied || !usable) {
    marks?.remove();
    return;
  }

  if (!marks) {
    marks = document.createElement('div');
    marks.className = 'sene-band__marks';
    marks.dataset.bf30Marks = '';
    band.querySelector(SELECTORS.bandFill)?.closest('.sene-band__track')?.after(marks);
  }

  // A step is reached once the fill has passed its position. Reached steps
  // get a filled dot, the rest stay as empty rings.
  const markFor = (position, reached, className, label) =>
    `<span class="sene-band__mark${className} ${reached ? 'is-reached' : ''}" style="left: ${position}%"><i></i>${label}</span>`;
  const free = markFor(BF30_SPLIT, fillPct >= BF30_SPLIT, ' sene-band__mark--free', 'Free shipping');
  // Tier steps sit at fixed, evenly spaced positions. The 20% step is reached as
  // soon as the cart has anything in it; the others once the fill passes them.
  const tiers = BF30_TIERS.map((tier, index) => {
    const position = BF30_SPLIT + 20 * (index + 1);
    const reached = index === 0 ? cartTotalCents() > 0 : fillPct >= position;
    return markFor(position, reached, '', `${tier.pct}%`);
  }).join('');
  marks.innerHTML = free + tiers;
}

function paintBand(band, { gapCents, cleared, thresholdCents }) {
  if (!band) return;

  band.dataset.cleared = cleared ? 'true' : 'false';

  if (bf30BandActive()) {
    band.dataset.bfTiers = 'true';
    paintBf30Band(band, { gapCents, cleared, thresholdCents });
    requestAnimationFrame(() => {
      band.dataset.seneResolved = 'true';
    });
    return;
  }
  delete band.dataset.bfTiers;
  band.querySelector('[data-bf30-band]')?.remove();

  const headline = band.querySelector(SELECTORS.bandHeadline);
  if (headline) {
    headline.textContent = cleared ? COPY.clearedHeadline : COPY.gapHeadline(moneyTrim(gapCents));
  }

  const entered = bf30Entered() && !document.documentElement.classList.contains('bf25');
  const fill = band.querySelector(SELECTORS.bandFill);
  const pct = entered
    ? Math.round(bf30Position(bf30Winning() ? Number(cart?.original_total_price) || 0 : cartTotalCents(), thresholdCents))
    : thresholdCents > 0
      ? Math.min(100, Math.round((cartTotalCents() / thresholdCents) * 100))
      : 0;
  if (fill) fill.style.width = `${pct}%`;
  paintBf30Marks(band, entered, thresholdCents, pct);

  // Let transitions run from here on: the first paint has landed, so any
  // further change is a real state change worth animating.
  requestAnimationFrame(() => {
    band.dataset.seneResolved = 'true';
  });
}

function paintShipping(cleared) {
  document.querySelectorAll(SELECTORS.shippingValue).forEach((node) => {
    node.textContent = cleared ? COPY.shippingFree : COPY.shippingPending;
    node.dataset.cleared = cleared ? 'true' : 'false';
  });
}

function paintEmptyState(thresholdCents) {
  document.querySelectorAll(SELECTORS.emptyThreshold).forEach((node) => {
    node.textContent = moneyTrim(thresholdCents);
  });
}

/** Matches a currency amount in announcement copy: an optional symbol or code,
 *  then digits with optional separators. Deliberately loose, because the bar is
 *  merchant-written free text. */
const MONEY_IN_TEXT = /(?:[$£€¥]|USD\s?)\s?\d[\d.,]*/;

const SHIPPING_PHRASE = /free\s+shipping/i;

/** Cap on how many times a displaced message is restored per page load. The
 *  system overwriting it has its own observer; without a ceiling the two could
 *  trade writes indefinitely. In practice one restore is enough, because
 *  Intelligems applies its edits once after load. */
const MAX_BAR_REPAIRS = 5;
let barRepairs = 0;

/**
 * Puts back announcement messages that something else has overwritten with a
 * free-shipping line.
 *
 * Intelligems targets this bar with onsite text edits pinned to positional
 * selectors (span:nth-of-type(9) and friends). Those positions were chosen
 * against the old marquee, whose track was duplicated four times. On the static
 * bar the same selectors land on different spans, so "Custom fit included free"
 * and "Made to order in Los Angeles" get replaced with a shipping line and the
 * bar says the same thing three times.
 *
 * The check is deliberately narrow: restore only when the message has been
 * replaced by free-shipping copy and was not free-shipping copy to begin with.
 * Any other edit is left alone, so this does not block copy tests on this bar.
 *
 * This becomes a no-op once those eight Intelligems onsite edits are removed,
 * which is the actual fix.
 */
function barTracks() {
  return Array.from(document.querySelectorAll('[data-sene-bar-templates]'));
}

/**
 * What a given announcement message should read, given the live threshold.
 * @param {string} template The merchant's raw text, [threshold] token intact.
 * @param {string} value Formatted threshold.
 * @returns {string}
 */
function expectedBarText(template, value) {
  if (template.includes('[threshold]')) return template.replace(/\[threshold\]/g, value);
  if (SHIPPING_PHRASE.test(template) && MONEY_IN_TEXT.test(template)) {
    return template.replace(MONEY_IN_TEXT, value);
  }
  return template;
}

/**
 * @returns {Array<{node: Element, target: Element, expected: string, isThresholdLine: boolean}>}
 */
function barMessages(value) {
  const out = [];

  for (const track of barTracks()) {
    let templates;
    try {
      templates = JSON.parse(track.dataset.seneBarTemplates || '[]');
    } catch (error) {
      continue;
    }
    if (!Array.isArray(templates)) continue;

    const items = Array.from(track.querySelectorAll('.cfrt-bar__item'));
    items.forEach((node, index) => {
      const template = templates[index];
      if (typeof template !== 'string') return;
      out.push({
        node,
        target: node.querySelector('a') || node,
        expected: expectedBarText(template, value),
        isThresholdLine: template.includes('[threshold]') || SHIPPING_PHRASE.test(template),
      });
    });
  }

  return out;
}

/**
 * Restores announcement messages that something else has overwritten.
 *
 * Intelligems targets this bar with onsite text edits pinned to positional
 * selectors (span:nth-of-type(9) and friends), chosen against the old marquee
 * whose track was duplicated four times. On the static bar those positions land
 * on different spans, so "Custom fit included free" and "Made to order in Los
 * Angeles" get replaced with a shipping line and the bar says free shipping
 * three times.
 *
 * Our own threshold line is always asserted - it is idempotent and derives from
 * Intelligems' offer anyway. Other messages are only restored when they have
 * been replaced by free-shipping copy, so ordinary copy tests on this bar still
 * work, and the restore is capped because the other system has its own observer
 * and two uncapped writers would trade forever.
 *
 * All of this becomes a no-op once those eight Intelligems onsite edits are
 * deleted, which is the real fix.
 */
function repairDisplacedShippingMessages(value) {
  for (const { target, expected, isThresholdLine } of barMessages(value)) {
    const current = target.textContent || '';
    if (current.trim() === expected.trim()) continue;

    if (isThresholdLine) {
      target.textContent = expected;
      continue;
    }

    if (!SHIPPING_PHRASE.test(current)) continue;
    if (barRepairs >= MAX_BAR_REPAIRS) continue;

    target.textContent = expected;
    barRepairs += 1;
  }
}

/**
 * Keeps the announcement bar's free-shipping figure equal to the figure the
 * drawer states and checkout charges.
 *
 * Two forms are supported: an explicit [threshold] token, re-rendered from the
 * stored template, and legacy copy that merely mentions shipping and contains
 * an amount, where the amount is swapped in place.
 *
 * Writes are idempotent and always derive from the ORIGINAL text, so this can
 * run on every repaint without compounding.
 */
/**
 * Hides an announcement separator that has wrapped to the end of a line.
 *
 * The static bar wraps on narrow viewports - at 375px the three messages need
 * 616px on one line - and the middot between the last two messages was left
 * dangling at the end of the first line, reading as a typo.
 *
 * Uses `visibility` rather than `display`, deliberately: collapsing a
 * separator would change the wrapping that decided it was dangling in the
 * first place, and the next pass could re-show it. Visibility leaves the box
 * in place, so the layout is stable and this is safe to run on every resize.
 * Separators are reset first so widening the window brings them back.
 */
function tidyBarSeparators() {
  for (const track of barTracks()) {
    const kids = Array.from(track.children);

    for (const kid of kids) {
      if (kid.classList.contains('cfrt-bar__dot')) kid.style.visibility = '';
    }

    kids.forEach((node, index) => {
      if (!node.classList.contains('cfrt-bar__dot')) return;

      // Compare the ITEMS either side of the separator, not the separator
      // against an item: a dot and a message sit on the same visual line but
      // report different offsetTop, because they are different heights inside
      // the same flex line. Comparing those directly hid every separator.
      const prev = kids[index - 1];
      const next = kids[index + 1];
      if (!prev || !next) return;

      // Its neighbours are on different lines, so this separator ended a line.
      if (prev.offsetTop !== next.offsetTop) node.style.visibility = 'hidden';
    });
  }
}

/**
 * True inside the theme editor.
 *
 * The announcement-bar repair below rewrites any "free shipping over $X" line
 * to the visitor's resolved threshold. On the storefront that is the whole
 * point. In the theme editor it is actively hostile: the merchant types a new
 * amount into the announcement setting, the section re-renders, and this
 * immediately overwrites it with the resolved value - so the edit appears to
 * revert itself and the number looks stuck. Reported 2026-09-03.
 *
 * Separator tidying stays on in the editor: it only toggles visibility of
 * wrapped middots and never changes text the merchant is editing.
 */
function inThemeEditor() {
  return Boolean(window.Shopify && window.Shopify.designMode);
}

function paintAnnouncements(thresholdCents) {
  if (!inThemeEditor()) {
    repairDisplacedShippingMessages(moneyTrim(thresholdCents));
  }
  tidyBarSeparators();
}

/**
 * The substance of the design: what order the tiles go in, which are shown,
 * and which one - if any - earns promotion to lead tile.
 *
 * Kept pure and exported so it can be tested directly on numbers, without a
 * DOM. Nothing here reads or writes the page.
 *
 * @param {{items: Array<{price: number}>, gapCents: number, cleared: boolean,
 *          merchSubtotalCents: number, maxTiles: number}} input
 * @returns {{ranked: Array, visible: Array, lead: object|null, everyVisibleClears: boolean}}
 */
export function rankTiles({ items, gapCents, cleared, merchSubtotalCents, maxTiles }) {
  // Items that CLEAR the remaining gap come first, cheapest of those first,
  // then everything else cheapest first. A $28 hat is closest to a $31 gap and
  // still leaves the customer $3 short - the worst outcome, because they paid
  // and got nothing. Proximity is the wrong sort key; sufficiency is the right
  // one.
  const ranked = items.slice().sort((a, b) => {
    const aClears = !cleared && a.price >= gapCents;
    const bClears = !cleared && b.price >= gapCents;
    if (aClears !== bClears) return aClears ? -1 : 1;
    return a.price - b.price;
  });

  const visible = ranked.slice(0, maxTiles);

  // A lead tile is promoted only when it clears the gap AND costs no more than
  // what the customer has already committed to. Without the second condition a
  // $14 socks order would lead with the $87 tie that happens to close its $86
  // gap, which is an absurd thing to recommend.
  const first = visible[0];
  const lead =
    !cleared && first && first.price >= gapCents && first.price <= merchSubtotalCents ? first : null;

  const everyVisibleClears =
    !cleared && visible.length > 0 && visible.every((entry) => entry.price >= gapCents);

  return { ranked, visible, lead, everyVisibleClears };
}

function paintCrossSell(root, { gapCents, cleared, thresholdCents }) {
  if (!root) return;

  const tiles = Array.from(root.querySelectorAll(SELECTORS.tile));
  if (!tiles.length) {
    root.hidden = true;
    return;
  }

  const maxTiles = Number(root.dataset.maxTiles) || 6;

  const { ranked, lead, everyVisibleClears } = rankTiles({
    items: tiles.map((tile) => ({ tile, price: Number(tile.dataset.priceCents) || 0 })),
    gapCents,
    cleared,
    merchSubtotalCents: merchandiseSubtotalCents(),
    maxTiles,
  });

  ranked.forEach((entry, index) => {
    const isVisible = index < maxTiles;
    entry.tile.hidden = !isVisible;

    // Close a sheet only when its own tile is dropping out of the row.
    //
    // A blanket closeAllOptions() here was wrong: paint() runs for reasons that
    // have nothing to do with the tiles - the drawer opening, Intelligems
    // resolving a threshold, any cart-adjacent mutation - and each one yanked
    // the picker shut while the shopper was mid-choice. After a real section
    // morph these are fresh nodes with closed panels anyway, so there is
    // nothing to preserve and nothing to force.
    if (!isVisible) {
      const panel = entry.tile.querySelector(SELECTORS.options);
      if (panel) panel.hidden = true;
    }
    // Reorder with flex `order` rather than moving nodes: the drawer is
    // re-rendered by the theme's section morph on every cart change, and
    // shuffled DOM confuses it. Visual order changes, DOM order does not.
    entry.tile.style.order = String(index);
    entry.tile.dataset.position = String(index + 1);

    const helper = entry.tile.querySelector(SELECTORS.helper);

    if (cleared || !isVisible) {
      // Once the threshold is cleared all the arithmetic drops out: no
      // shortfalls, no promotion.
      if (helper) {
        helper.hidden = true;
        helper.textContent = '';
        delete helper.dataset.clears;
      }
      return;
    }

    const clearsGap = entry.price >= gapCents;
    if (helper) {
      helper.hidden = false;
      helper.textContent = clearsGap ? COPY.helperClears : COPY.helperShort(moneyTrim(gapCents - entry.price));
      helper.dataset.clears = clearsGap ? 'true' : 'false';
    }
  });

  const eyebrow = root.querySelector(SELECTORS.eyebrow);
  if (eyebrow) eyebrow.textContent = lead ? COPY.eyebrowLead : COPY.eyebrowPlain;

  const note = root.querySelector(SELECTORS.note);
  if (note) {
    // No sub-note for the lead-tile case any more (Ray, 2026-09-03: dropped
    // "The first item clears the gap on its own." along with the sum box -
    // the row title change to "Add to get free shipping" already says it).
    let text = '';
    if (cleared) {
      const subject = clearedNoteSubject();
      text = subject ? COPY.noteClearedWith(subject) : COPY.noteCleared;
    } else if (everyVisibleClears) {
      text = COPY.noteAllClear(moneyTrim(thresholdCents));
    }
    note.textContent = text;
    note.hidden = text === '';
  }

  root.hidden = false;
  observeImpressions(root);
}

/**
 * Puts the drawer's scroll offsets back after a morph. Runs on every paint
 * because the morph can land after the add resolves; gives up once the
 * offsets are in place or the attempts run out, so a scroller that genuinely
 * cannot reach the offset (its content shrank) does not pin scrolling forever.
 */
function restorePendingScroll() {
  if (!pendingScroll) return;

  const strip = document.querySelector(SELECTORS.strip);
  const scroller = document.querySelector(SELECTORS.scroller);

  if (strip && strip.scrollLeft !== pendingScroll.stripLeft) {
    strip.scrollLeft = pendingScroll.stripLeft;
  }
  if (scroller && scroller.scrollTop !== pendingScroll.scrollTop) {
    scroller.scrollTop = pendingScroll.scrollTop;
  }

  const settled =
    (!strip || strip.scrollLeft === pendingScroll.stripLeft) &&
    (!scroller || scroller.scrollTop === pendingScroll.scrollTop);

  pendingScroll.attempts -= 1;
  if (settled || pendingScroll.attempts <= 0) pendingScroll = null;
}

function paint() {
  if (painting) return;
  painting = true;

  try {
    const band = document.querySelector(SELECTORS.band);
    const config = configElement();
    moneyFormat = config?.dataset.moneyFormat || moneyFormat;

    const { cents: thresholdCents, source } = resolveThreshold();
    if (band) band.dataset.thresholdResolvedFrom = source;

    paintEmptyState(thresholdCents);
    paintAnnouncements(thresholdCents);

    if (!cart) return;

    const gapCents = Math.max(0, thresholdCents - cartTotalCents());
    const cleared = gapCents === 0 && cartTotalCents() > 0;
    const context = { gapCents, cleared, thresholdCents };

    paintBand(band, context);
    paintShipping(cleared);

    document.querySelectorAll(SELECTORS.xsell).forEach((root) => {
      paintCrossSell(root, context);
      root.dataset.senePainted = `${thresholdCents}:${cartTotalCents()}:${cart.item_count}`;
    });

    reportThresholdCrossing(cleared, gapCents, thresholdCents);
    restorePendingScroll();
  } finally {
    painting = false;
  }
}

/* ------------------------------------------------------- saved-fit gate --- */

/** App Proxy route to the hub, same one assets/saved-fit.js uses on the PDP.
 *  Shopify signs the customer id into the query string server-side, so the
 *  browser never names a customer and the answer cannot be spoofed. */
const SAVED_FIT_ENDPOINT = '/apps/hub/saved-fit/';
const FIT_CHECK_TIMEOUT_MS = 5000;

/**
 * Does the hub confirm a usable saved fit for THIS product type?
 *
 * The Liquid tag gate upstream is a cheap pre-filter that stops us rendering an
 * offer we cannot honour. It is not trustworthy on its own: `saved_customfit_*`
 * disagrees with the real fit store in both directions. Proven false positives
 * include a 2019 Kickstarter bulk import - twenty accounts created within forty
 * seconds carrying blazer + pants + suit tags, zero orders in seven years. Those
 * accounts pass the tag gate and have no fit for the factory to cut.
 *
 * So the tag decides what to SHOW and the hub decides what may be ADDED.
 *
 * Note this deliberately INVERTS saved-fit.js's failure posture. There, an
 * unreachable hub resolves to UNKNOWN and leaves the PDP untouched, because the
 * cost of being wrong is a missing card. Here the cost of being wrong is an
 * order the factory cannot fulfil, so anything short of an explicit yes is a no.
 *
 * @param {Element} tile
 * @returns {Promise<boolean>}
 */
async function hubConfirmsFit(tile) {
  const productId = tile.dataset.productId;
  const productType = tile.dataset.productType;
  if (!productId) return false;

  const url = `${SAVED_FIT_ENDPOINT}?product_id=${encodeURIComponent(productId)}&product_type=${encodeURIComponent(productType ?? '')}`;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), FIT_CHECK_TIMEOUT_MS) : null;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: controller?.signal,
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (!data || typeof data.has_fit !== 'boolean') return false;

    /*
     * has_fit alone. Deliberately NOT also requiring first_in_category === false.
     *
     * Read hub_2.0 sene_hub/core/handlers/measurement.py:709 before changing
     * this. The hub treats sizing_method None, "" and "Use Saved Fit" as the
     * same saved-fit path, and when no FinishedMeasurement exists for the
     * product type it does not fail - it derives a new one from the customer's
     * BodyMeasurement and logs it as NEW_FIT. The genuinely fatal case is the
     * absence of a BodyMeasurement, which raises MeasurementException.
     *
     * saved_fit.py's has_fit is true when a fit, a BodyMeasurement, OR a Quiz
     * exists, which is exactly the set the hub can fulfil. first_in_category
     * only drives which selectors the PDP shows; requiring it false here would
     * have blocked every quiz-taker who has not yet ordered that garment - the
     * large majority of the audience, and orders the factory handles fine.
     */
    return data.has_fit === true;
  } catch (error) {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/* -------------------------------------------------- variant selection --- */

/**
 * Parsed variant matrices, keyed by tile. A WeakMap rather than a dataset flag
 * because the section morph throws these nodes away on every cart change, and
 * a WeakMap lets the old entries go with them.
 * @type {WeakMap<Element, Array<{id: number, o: string[], a: boolean, p: number}>>}
 */
const variantCache = new WeakMap();

/**
 * The variant matrix Liquid emitted for a tile, or an empty array for a
 * single-variant tile that carries none.
 * @param {Element} tile
 */
function variantMatrix(tile) {
  const cached = variantCache.get(tile);
  if (cached) return cached;

  const node = tile.querySelector(SELECTORS.variants);
  let parsed = [];
  if (node) {
    try {
      parsed = JSON.parse(node.textContent || '[]');
    } catch (error) {
      // A malformed matrix must not take the whole row down with it. The tile
      // simply stays unresolvable, so its Add button never enables.
      parsed = [];
    }
  }

  variantCache.set(tile, parsed);
  return parsed;
}

/**
 * The shopper's current choices as a map of option position -> value.
 *
 * Only options that RENDERED a fieldset appear. Single-value options are
 * deliberately absent: they have exactly one possible value, so leaving them
 * unconstrained matches the same variants while keeping the comparison simple.
 * That is what makes "Charcoal / Jacket Only" resolve from one colour click.
 * @param {Element} tile
 * @returns {Map<number, string>}
 */
function readSelection(tile) {
  const selection = new Map();

  tile.querySelectorAll('[data-xsell-option]').forEach((group) => {
    const position = Number(group.dataset.optionPosition);
    if (!position) return;

    const chosen = group.querySelector('[data-xsell-option-value][aria-pressed="true"]');
    if (chosen) selection.set(position, chosen.dataset.value);
  });

  return selection;
}

/**
 * Does this variant satisfy every constraint in `selection`?
 * @param {{o: string[]}} variant
 * @param {Map<number, string>} selection
 */
function variantMatches(variant, selection) {
  for (const [position, value] of selection) {
    if (variant.o?.[position - 1] !== value) return false;
  }
  return true;
}

/**
 * The available variant matching every current choice, or null while any
 * rendered option is still unchosen.
 * @param {Element} tile
 */
function resolveVariant(tile) {
  const groups = tile.querySelectorAll('[data-xsell-option]');
  const selection = readSelection(tile);

  // Every rendered selector must have an answer. Without this a two-option
  // product would resolve off the colour alone and add an arbitrary size.
  if (selection.size !== groups.length) return null;

  return variantMatrix(tile).find((variant) => variant.a && variantMatches(variant, selection)) ?? null;
}

/**
 * Greys out option values that cannot be reached given the OTHER current
 * choices, then updates the confirm button and the displayed price.
 *
 * Each value is tested against the selection with its own position removed, so
 * clicking a colour narrows the sizes without the colour disabling itself.
 * @param {Element} tile
 */
function refreshOptionState(tile) {
  const matrix = variantMatrix(tile);
  const selection = readSelection(tile);

  tile.querySelectorAll('[data-xsell-option-value]').forEach((button) => {
    const position = Number(button.dataset.optionPosition);
    const value = button.dataset.value;

    const others = new Map(selection);
    others.delete(position);

    const reachable = matrix.some(
      (variant) => variant.a && variant.o?.[position - 1] === value && variantMatches(variant, others)
    );

    button.disabled = !reachable;
  });

  const resolved = resolveVariant(tile);
  const confirm = tile.querySelector(SELECTORS.confirm);

  if (confirm) {
    confirm.disabled = !resolved;
    confirm.textContent = resolved ? 'Add to bag' : 'Select an option';
  }

  // Keep the tile's advertised price honest once a variant is picked. The
  // rendered price is the cheapest available variant, so a dearer size would
  // otherwise still show the floor price.
  const priceNode = tile.querySelector(SELECTORS.price);
  if (priceNode && resolved) {
    if (!priceNode.dataset.basePrice) priceNode.dataset.basePrice = priceNode.textContent ?? '';
    priceNode.textContent = moneyTrim(resolved.p);
  }
}

/**
 * @param {Element} tile
 */
function openOptions(tile) {
  closeAllOptions();

  const panel = tile.querySelector(SELECTORS.options);
  if (!panel) return;

  panel.hidden = false;
  refreshOptionState(tile);

  /**
   * Fit the sheet to the drawer's visible window, not to the row.
   *
   * The row is about 331px tall (a 132px thumbnail plus name, price, helper
   * and button) while the drawer's scroller only shows about 257px. A sheet
   * sized to the row therefore ALWAYS hangs off one end: measured live, the
   * title sat above the scrollport and the Add button 74px below it, behind
   * the checkout footer.
   *
   * So pin the row's top to the top of the scrollport and cap the sheet at the
   * scrollport's height. The sheet then occupies exactly the visible window,
   * its sticky header and footer stay put, and any overflow scrolls inside.
   */
  const row = tile.closest(SELECTORS.xsell);
  const scroller = document.querySelector(SELECTORS.scroller);

  if (row && scroller) {
    row.scrollIntoView({ block: 'start' });
    panel.style.maxHeight = `${scroller.clientHeight}px`;
  }

  const firstValue = panel.querySelector('[data-xsell-option-value]:not([disabled])');
  if (firstValue instanceof HTMLElement) firstValue.focus({ preventScroll: true });
}

function closeAllOptions() {
  document.querySelectorAll(SELECTORS.options).forEach((panel) => {
    panel.hidden = true;
    panel.style.maxHeight = '';

    // Put the advertised price back to the cheapest-available figure Liquid
    // rendered. Without this a tile abandoned mid-selection keeps showing the
    // dearer variant's price while data-price-cents - what rankTiles sorts and
    // writes the "still $X short" helper from - still holds the floor.
    const tile = panel.closest(SELECTORS.tile);
    const priceNode = tile?.querySelector(SELECTORS.price);
    if (priceNode?.dataset.basePrice) {
      priceNode.textContent = priceNode.dataset.basePrice;
      delete priceNode.dataset.basePrice;
    }
  });
}

/* -------------------------------------------------------------- add flow --- */

/**
 * Adds a tile's variant through the AJAX cart API and lets the theme's own
 * section morph put the new line in place. The drawer stays open, nothing
 * reloads, and both scroll positions are restored across the re-render.
 * @param {HTMLElement} tile
 */
async function addTile(tile) {
  if (addInFlight) return;

  // A tile carrying selectors must resolve to a concrete variant. Falling back
  // to dataset.variantId would add the CHEAPEST variant - an arbitrary size the
  // shopper never picked - because that is the value Liquid renders for
  // ranking.
  const needsOptions = tile.hasAttribute('data-needs-options');
  const resolved = needsOptions ? resolveVariant(tile) : null;
  if (needsOptions && !resolved) return;

  const variantId = resolved ? String(resolved.id) : tile.dataset.variantId;
  if (!variantId) return;

  /**
   * Line-item properties, for made-to-measure tiles only.
   *
   * Sent because it is the correct, PDP-matching shape for a saved-fit line -
   * NOT because omitting it breaks the order. An earlier version of this
   * comment claimed the latter and it does not hold up: the hub treats a
   * missing Sizing Method the same as "Use Saved Fit"
   * (hub_2.0 measurement.py:709), and 14 of 16 MTM lines carrying no
   * properties at all shipped normally in an Apr-Jun 2026 sample.
   *
   * The abort below is therefore about self-consistency, not rescue: if a tile
   * says it is made-to-measure but its property blob will not parse, something
   * upstream is wrong and guessing is the worst response. The real
   * fulfilment gate is hubConfirmsFit().
   */
  let properties = null;
  const rawProperties = tile.dataset.addProperties;

  if (rawProperties) {
    try {
      properties = JSON.parse(rawProperties);
    } catch (error) {
      showAddError(tile);
      return;
    }
  }

  if (tile.dataset.fitKind === 'customfit' && !properties) {
    showAddError(tile);
    return;
  }

  const button = tile.querySelector(SELECTORS.add);
  const strip = tile.closest(SELECTORS.strip);
  const scroller = document.querySelector(SELECTORS.scroller);

  pendingScroll = {
    stripLeft: strip ? strip.scrollLeft : 0,
    scrollTop: scroller ? scroller.scrollTop : 0,
    attempts: 8,
  };

  const beforeTotal = cartTotalCents();
  const { cents: thresholdBefore } = resolveThreshold();
  const gapBefore = Math.max(0, thresholdBefore - beforeTotal);

  addInFlight = true;
  if (button) button.disabled = true;

  const confirmButton = tile.querySelector(SELECTORS.confirm);
  if (confirmButton) confirmButton.disabled = true;

  // Made-to-measure: ask the hub before committing. Done here, after the
  // buttons are disabled, so the ~500ms round trip reads as the add working
  // rather than as a dead tap. Cheap to run because it only fires on an
  // explicit add of an MTM tile, never on render.
  if (tile.dataset.fitKind === 'customfit') {
    const confirmed = await hubConfirmsFit(tile);

    if (!confirmed) {
      // Drop the tile as well as explaining. Leaving it clickable invites the
      // shopper to keep tapping something that will never work.
      tile.hidden = true;
      closeAllOptions();
      showAddError(tile, COPY.fitError);
      addInFlight = false;
      if (button) button.disabled = false;
      return;
    }
  }

  // Ask for the same sections the theme's own add-to-cart asks for, so the
  // response can drive the identical morph path.
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((node) => node.dataset.sectionId)
    .filter(Boolean);

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          properties
            ? { id: Number(variantId), quantity: 1, properties }
            : { id: Number(variantId), quantity: 1 },
        ],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const payload = await response.json();

    if (!response.ok || payload.status) {
      // Out of stock or otherwise refused: drop the tile so it cannot be
      // clicked again, and say so once.
      tile.hidden = true;
      showAddError(tile);
      return;
    }

    closeAllOptions();
    // variantId and resolved are the values this add actually used - see the
    // comment on trackAdd for why tile.dataset.priceCents (the CHEAPEST
    // available variant, per snippets/cart-crosssell.liquid) cannot be used
    // to log price or variant_id here. resolved is null when the tile had no
    // picker, in which case tile.dataset.priceCents IS the added variant's
    // price - there was only ever one variant to choose from.
    trackAdd(tile, gapBefore, thresholdBefore, variantId, resolved?.p ?? (Number(tile.dataset.priceCents) || 0));
    markDrawerAddInSession();
    crossingCameFromDrawerAdd = true;

    await refreshCart();

    // CartAddEvent's eventName IS ThemeEvents.cartUpdate, so dispatching it on
    // document drives cart-items-component's existing morph. Dispatching on
    // document rather than on the component matters: the component ignores
    // events whose target is itself.
    document.dispatchEvent(
      new CartAddEvent({}, 'sene-cart-crosssell', {
        source: 'sene-cart-crosssell',
        itemCount: 1,
        productId: tile.dataset.productId,
        variantId,
        sections: payload.sections,
      })
    );

    requestAnimationFrame(() => paint());
  } catch (error) {
    showAddError(tile);
  } finally {
    addInFlight = false;
    if (button) button.disabled = false;

    // A failed add leaves the sheet open, so the confirm button has to come
    // back too or the shopper is looking at a dead control with no way to
    // retry. Re-derive rather than blindly enabling: if the selection is no
    // longer resolvable it should stay disabled.
    if (confirmButton && !tile.hidden) refreshOptionState(tile);
  }
}

function showAddError(tile, message) {
  const root = tile.closest(SELECTORS.xsell);
  if (!root) return;

  let node = root.querySelector('.sene-xsell__error');
  if (!node) {
    node = document.createElement('p');
    node.className = 'sene-xsell__error';
    node.setAttribute('role', 'status');
    root.appendChild(node);
  }
  node.hidden = false;
  node.textContent = message ?? COPY.addError;
}

/* ------------------------------------------------------- instrumentation --- */

let lastClearedState = null;
let lastTotalAtPaint = null;
/** Set the moment a drawer add succeeds and consumed by the next crossing, so
 *  attribution means "this add crossed it" rather than "an add happened at
 *  some point this session". */
let crossingCameFromDrawerAdd = false;

function emit(name, params) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({ event: name, ...params });
    }
  } catch (error) {
    /* Instrumentation must never break the drawer. */
  }
}

/** Marks the session as having taken a drawer add, so AOV can be split by it
 *  downstream. Also set as a GA4 user property so it lands on the purchase
 *  event without needing a join. */
function markDrawerAddInSession() {
  try {
    sessionStorage.setItem('sene_drawer_add', '1');
  } catch (error) {
    /* Private mode: the event still fires, only the session flag is lost. */
  }
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('set', 'user_properties', { sene_drawer_add: 'true' });
    }
  } catch (error) {
    /* ignore */
  }
  if (window.dataLayer && typeof window.dataLayer.push === 'function') {
    window.dataLayer.push({ sene_drawer_add: true });
  }
}

function hadDrawerAdd() {
  try {
    return sessionStorage.getItem('sene_drawer_add') === '1';
  } catch (error) {
    return false;
  }
}

/**
 * Fires on a real click on the Shop Pay button, whether that click lands or
 * navigation is still in flight. Purely observational - never preventDefault
 * or stopPropagation here, so the click proceeds to shop.app exactly as it
 * would with no listener attached.
 *
 * Verified live (2026-09-03) that a document-level capture listener sees this
 * click cleanly: event.target is the shop-pay-wallet-button element itself,
 * composedPath has no shadow-root retargeting or iframe boundary, and the
 * click is a real same-document navigation to shop.app - not an in-page
 * iframe overlay. So this is a reliable signal, not a guess.
 *
 * `location` separates the cart drawer from the identical button on the
 * standalone /cart page (both render snippets/cart-summary.liquid), which is
 * the whole reason to instrument this rather than read it off Shopify's
 * store-wide "Shop Pay payments" report.
 *
 * @param {boolean} inDrawer
 */
function trackShopPayClick(inDrawer) {
  emit('sene_shop_pay_click', {
    location: inDrawer ? 'cart_drawer' : 'cart_page',
    cart_total: cartTotalCents() / 100,
    item_count: cart?.item_count ?? null,
  });
}

/**
 * @param {HTMLElement} tile
 * @param {number} gapBefore
 * @param {number} thresholdCents
 * @param {string} variantId - The variant this add actually placed in the
 *   cart. NOT tile.dataset.variantId: that attribute is always the CHEAPEST
 *   available variant (snippets/cart-crosssell.liquid renders it that way for
 *   ranking), so a shopper who picked a dearer size or colour would otherwise
 *   have that pick logged as the tile's floor variant.
 * @param {number} priceCents - The price of `variantId`, in cents. Same
 *   reasoning: reading tile.dataset.priceCents here would log the floor
 *   price, and cleared_gap below would then disagree with `price` about
 *   whether this add actually closed the gap.
 */
function trackAdd(tile, gapBefore, thresholdCents, variantId, priceCents) {
  emit('sene_cart_tile_add', {
    position: Number(tile.dataset.position) || null,
    variant_id: variantId,
    product_id: tile.dataset.productId,
    item_name: tile.dataset.title,
    price: priceCents / 100,
    gap_before: gapBefore / 100,
    threshold: thresholdCents / 100,
    cleared_gap: priceCents >= gapBefore,
    // The rendered floor price at add time - what rankTiles sorted on and
    // paint() displayed - kept alongside `price` rather than in place of it,
    // so "promote items that close the gap" can be evaluated against what the
    // shopper actually saw on the tile, not what they ended up picking. No
    // other event carries both: sene_cart_tile_impression has the displayed
    // price but not the gap at that moment.
    displayed_price: (Number(tile.dataset.priceCents) || 0) / 100,
  });
}

function reportThresholdCrossing(cleared, gapCents, thresholdCents) {
  const total = cartTotalCents();

  if (lastClearedState === null) {
    lastClearedState = cleared;
    lastTotalAtPaint = total;
    return;
  }

  // A crossing is a change in the CART, not a change in what we know about the
  // threshold. Intelligems resolves after first paint and can legitimately move
  // the target from the $150 fallback down to $125, which flips `cleared` on a
  // cart nobody touched. Counting that as a crossing would inflate the metric
  // this module exists to move.
  const cartChanged = total !== lastTotalAtPaint;
  const wasCleared = lastClearedState;

  lastClearedState = cleared;
  lastTotalAtPaint = total;

  // Dropping back under the threshold ends the current attribution window.
  if (!cleared) {
    crossingCameFromDrawerAdd = false;
    return;
  }

  // Already cleared and still cleared is not a crossing, however much the cart
  // moved underneath it.
  if (wasCleared) return;
  if (!cartChanged) return;

  emit('sene_cart_threshold_crossed', {
    threshold: thresholdCents / 100,
    cart_total: total / 100,
    from_drawer_add: crossingCameFromDrawerAdd,
    session_had_drawer_add: hadDrawerAdd(),
  });

  crossingCameFromDrawerAdd = false;
}

function observeImpressions(root) {
  if (!('IntersectionObserver' in window)) return;

  if (!impressionObserver) {
    impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const tile = entry.target;
          const key = `${tile.dataset.variantId}:${tile.dataset.position}`;
          if (seenImpressions.has(key)) return;
          seenImpressions.add(key);
          emit('sene_cart_tile_impression', {
            position: Number(tile.dataset.position) || null,
            variant_id: tile.dataset.variantId,
            product_id: tile.dataset.productId,
            item_name: tile.dataset.title,
            price: (Number(tile.dataset.priceCents) || 0) / 100,
          });
        });
      },
      { threshold: 0.5 }
    );
  }

  root.querySelectorAll(`${SELECTORS.tile}:not([hidden])`).forEach((tile) => {
    impressionObserver.observe(tile);
  });
}

/**
 * True when an announcement message no longer shows the figure we last wrote.
 * Cheap enough to run from a MutationObserver: it reads attributes and text on
 * at most a handful of nodes.
 * @returns {boolean}
 */
/**
 * True when the empty-cart copy still shows a threshold we no longer believe.
 * The band and the cross-sell row do not exist on an empty cart, so without
 * this the empty state had nothing watching it and kept whatever the server
 * rendered - it sat on the $150 fallback while the header said $100.
 * @returns {boolean}
 */
function emptyStateDrifted() {
  const nodes = document.querySelectorAll(SELECTORS.emptyThreshold);
  if (!nodes.length) return false;

  syncMoneyFormat();
  const value = moneyTrim(resolveThreshold().cents);

  for (const node of nodes) {
    if ((node.textContent || '').trim() !== value) return true;
  }
  return false;
}

function announcementsDrifted() {
  // Never schedule a repaint for announcement drift inside the theme editor -
  // the "drift" there is the merchant typing, and repainting would fight them.
  if (inThemeEditor()) return false;

  syncMoneyFormat();
  const { cents } = resolveThreshold();
  const value = moneyTrim(cents);

  for (const { target, expected, isThresholdLine } of barMessages(value)) {
    const current = target.textContent || '';
    if (current.trim() === expected.trim()) continue;
    if (isThresholdLine) return true;
    if (SHIPPING_PHRASE.test(current) && barRepairs < MAX_BAR_REPAIRS) return true;
  }

  return false;
}

/* ----------------------------------------------------------------- wiring -- */

let repaintTimer = null;
let pendingRefetch = false;

/**
 * Coalesces repaint requests. The refetch flag is STICKY across coalesced
 * calls: a cart:update asking for fresh cart data must not be downgraded by a
 * DOM mutation that happens to land in the same debounce window. Losing that
 * flag painted the whole row against a stale cart.
 * @param {boolean} refetch
 */
function scheduleRepaint(refetch) {
  pendingRefetch = pendingRefetch || refetch;
  clearTimeout(repaintTimer);
  repaintTimer = setTimeout(async () => {
    const shouldRefetch = pendingRefetch;
    pendingRefetch = false;
    if (shouldRefetch) await refreshCart();
    paint();
  }, 60);
}

function init() {
  refreshCart().then(paint);

  // Escape closes the option sheet before it closes the drawer. Captured so it
  // runs ahead of dialog.js's own Escape handler, and only stopped when a sheet
  // was actually open - otherwise Escape would stop closing the drawer.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;

      const open = document.querySelector(`${SELECTORS.options}:not([hidden])`);
      if (!open) return;

      event.preventDefault();
      event.stopPropagation();
      closeAllOptions();
    },
    { capture: true }
  );

  // Adds. Delegated so it survives every section morph.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Option sheet, checked before the Add button because these controls live
    // inside the same tile.
    const optionValue = target.closest(SELECTORS.optionValue);
    if (optionValue instanceof HTMLElement && !optionValue.disabled) {
      const tile = optionValue.closest(SELECTORS.tile);
      if (tile) {
        event.preventDefault();

        const position = optionValue.dataset.optionPosition;
        const wasPressed = optionValue.getAttribute('aria-pressed') === 'true';

        // Clear the rest of this option's row, then toggle. Re-clicking the
        // chosen value deselects it, which is how a shopper backs out of a
        // colour that has left them no available sizes.
        tile
          .querySelectorAll(`[data-xsell-option-value][data-option-position="${position}"]`)
          .forEach((node) => node.setAttribute('aria-pressed', 'false'));

        optionValue.setAttribute('aria-pressed', wasPressed ? 'false' : 'true');
        refreshOptionState(tile);
      }
      return;
    }

    const confirm = target.closest(SELECTORS.confirm);
    if (confirm instanceof HTMLElement && !confirm.disabled) {
      const tile = confirm.closest(SELECTORS.tile);
      if (tile) {
        event.preventDefault();
        addTile(tile);
      }
      return;
    }

    if (target.closest(SELECTORS.optionsClose)) {
      event.preventDefault();
      closeAllOptions();
      return;
    }

    const button = target.closest(SELECTORS.add);
    if (button) {
      const tile = button.closest(SELECTORS.tile);
      if (tile) {
        event.preventDefault();
        // Multi-variant tiles ask before they add. Single-variant tiles keep
        // the original one-tap behaviour.
        if (tile.hasAttribute('data-needs-options')) {
          openOptions(tile);
        } else {
          addTile(tile);
        }
      }
      return;
    }

    const shopPayButton = target.closest(SELECTORS.shopPayButton);
    if (shopPayButton) trackShopPayClick(!!shopPayButton.closest(SELECTORS.drawer));

    // Anything else clicked inside the drawer may have changed the cart
    // without going through the theme's events - Redo's coverage toggle is
    // the case that matters here. Re-read rather than assume.
    if (target.closest(SELECTORS.drawer)) scheduleRepaint(true);
  });

  // The theme's own cart mutations. CartAddEvent and CartUpdateEvent share
  // this event name.
  document.addEventListener('cart:update', () => scheduleRepaint(true));
  document.addEventListener('cart:error', () => scheduleRepaint(true));

  // The section morph replaces our nodes wholesale, which drops the painted
  // marker along with every attribute we wrote. Repaint when that happens.
  // Guarded by `painting` and by the marker so our own writes cannot loop.
  const observer = new MutationObserver(() => {
    if (painting) return;

    const needsPaint =
      Array.from(document.querySelectorAll(SELECTORS.xsell)).some((root) => !root.dataset.senePainted) ||
      Array.from(document.querySelectorAll(SELECTORS.band)).some(
        (band) => band.dataset.seneResolved !== 'true'
      ) ||
      // Intelligems edits the announcement bar too, by positional selector, so
      // watch for our figure being overwritten and put it back.
      announcementsDrifted() ||
      emptyStateDrifted();

    // Our markers only go missing when the section was re-rendered, and the
    // section is only re-rendered because the cart changed - so refetch.
    // A cart change already refetched the cart, so only refetch if no fetch
    // happened in the last second. Otherwise every drawer re-render costs a second request.
    if (needsPaint) scheduleRepaint(Date.now() - lastCartFetchAt > 1000);
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Drawer open: impressions are counted once per open, not once per session.
  document.addEventListener(
    'dialog:open',
    () => {
      seenImpressions = new Set();
      impressionObserver?.disconnect();
      impressionObserver = null;
      scheduleRepaint(true);
    },
    true
  );

  // Rewrapping the announcement bar can strand a separator at a line end, or
  // free one that was stranded. Cheap and layout-stable, so it can just run.
  let barResizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(barResizeTimer);
    barResizeTimer = setTimeout(tidyBarSeparators, 120);
  });

  // Web fonts land after first paint and change the wrap point.
  if (document.fonts?.ready) document.fonts.ready.then(tidyBarSeparators).catch(() => {});

  // Intelligems resolves asynchronously. Repaint the moment it does, so the
  // fallback number is only ever on screen for a frame or two.
  //
  // onIgReady is an ACCESSOR on window, not a plain array: Intelligems' setter
  // is what drains pending callbacks, and it drains them immediately when it
  // has already loaded. Pushing into the existing array never trips the setter,
  // so a callback registered after Intelligems finished sat there forever and
  // the empty-cart copy kept the server's fallback figure. Assign a new array
  // instead - carrying the existing entries so nobody else's callback is lost.
  const pendingIgCallbacks = Array.isArray(window.onIgReady) ? window.onIgReady.slice() : [];
  pendingIgCallbacks.push(() => paint());
  window.onIgReady = pendingIgCallbacks;

  // And if it is already resolved by the time this runs, do not wait at all.
  if (window.igData?.offers?.getAll) scheduleRepaint(false);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export { IG_SHIPPING_EXPERIENCE_ID };
